import React, { useMemo } from 'react'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useGetDescribeQuery, useGetSupportedObjectsQuery } from '../../../store/integrations/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AIFormValue } from './EditAIFormView'
import { useFormContext } from 'react-hook-form'
import { FormRow, FormValues } from './CrmField'
import { generateId } from '../../../common/util'
import { DescriptorType } from '../../../models/AiFeature'
import { toMappingKey } from './utils'

interface FieldIntegrationRowProps {
  formRow: FormRow
  showFieldTitle?: boolean
  connector: string
  type: DescriptorType
  selectedObjects?: string[]
  objectSelectDisabled?: boolean
}

export const FieldIntegrationRow: React.FC<FieldIntegrationRowProps> = ({
  formRow,
  showFieldTitle = true,
  connector,
  type,
  selectedObjects,
  objectSelectDisabled = false,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { watch, setValue } = useFormContext<FormValues>()
  const formFields = watch('mappingFormFields')

  const { data: supportedObjects, isFetching: isSupportedObjectsLoading } = useGetSupportedObjectsQuery({
    accountId,
    connector: connector,
  })
  // Callers build `selectedObjects` inline, so it is a new array on every render. Keying the memo on its contents
  // instead keeps `objectOptions` referentially stable, which SelectDropdown relies on to not reset its selection.
  const selectedObjectsKey = selectedObjects ? selectedObjects.join('|') : null
  const objectOptions = useMemo<SelectOption[]>(() => {
    if (!supportedObjects) {
      return []
    }
    const allowedObjectNames = selectedObjectsKey === null ? null : new Set(selectedObjectsKey.split('|'))
    return supportedObjects
      .filter((object) => !allowedObjectNames || allowedObjectNames.has(object.name))
      .map((obj) => ({
        id: obj.name,
        label: `${obj.label} (${obj.name})`,
      }))
  }, [supportedObjects, selectedObjectsKey])

  const selectedObjectOption = useMemo(() => {
    const selectedField = formFields.find((field) => field.key === formRow.key)
    if (!selectedField) {
      return null
    }
    return objectOptions.find((option) => option.id === selectedField.objectName) ?? null
  }, [formRow.key, formFields, objectOptions])

  const handleObjectSelected = (option: SelectOption) => {
    const newFormFields = formFields.filter((formField) => formField.key !== formRow.key)
    newFormFields.push({ key: formRow.key, objectName: option.id, fieldName: null, value: formRow.value, type })
    const fieldObjects = newFormFields
      .filter((field) => field.type === 'field')
      .map((field) => field.objectName)
      .filter((value): value is string => value !== null)
    setValue(
      'mappingFormFields',
      newFormFields.filter((field) => field.objectName && fieldObjects.includes(field.objectName)),
    )
  }

  return (
    <div className='d-flex align-items-center mb-3'>
      {showFieldTitle && (
        <>
          <div style={{ width: '200px' }}>{formRow.value}</div>
          <div className='mx-2'>→</div>
        </>
      )}

      <SelectDropdown
        className='zocks-px-0.75 py-2'
        options={objectOptions}
        initialSelectedOption={selectedObjectOption}
        onOptionSelected={handleObjectSelected}
        disabled={isSupportedObjectsLoading || objectSelectDisabled}
        loading={isSupportedObjectsLoading}
        searchable
        placeholder='Select object...'
      />

      <span>/</span>

      <div className='col-md-6'>
        <FieldSelectDropdown formRow={formRow} connector={connector} object={selectedObjectOption?.id} type={type} />
      </div>
    </div>
  )
}

type FieldSelectDropdownProps = {
  formRow: FormRow
  connector: string
  object?: string | null
  type: DescriptorType
}

type CrmFormFieldDefinition = {
  name: string
  label: string
}

const FieldSelectDropdown: React.FC<FieldSelectDropdownProps> = ({ formRow, connector, object, type }) => {
  const accountId = useAppSelector(selectAccountId)
  const { watch, setValue } = useFormContext<FormValues>()
  const formFields = watch('mappingFormFields')
  const { data: describe, isFetching: isDescribeLoading } = useGetDescribeQuery(
    {
      accountId,
      connector,
      context: { objectName: object! },
    },
    { skip: !object },
  )

  const fieldOptions = useMemo<SelectOption[]>(() => {
    if (!describe) {
      return []
    }
    const formFieldDefinitions: CrmFormFieldDefinition[] = describe.data.describe.formFieldDefinitions
    return formFieldDefinitions.map((field) => ({ id: field.name, label: `${field.label} (${field.name})` }))
  }, [describe])

  const selectedFieldOption = useMemo(() => {
    const selectedField = formFields.find((field) => field.key === formRow.key)
    if (!selectedField) {
      return null
    }
    return fieldOptions.find((option) => option.id === selectedField.fieldName) ?? null
  }, [formRow.key, formFields, fieldOptions])

  const handleOptionSelected = (option: SelectOption) => {
    if (!object) {
      return
    }
    const newFormFields = formFields.filter((formField) => formField.key !== formRow.key)
    newFormFields.push({ key: formRow.key, objectName: object, fieldName: option.id, value: formRow.value, type: type })
    setValue('mappingFormFields', newFormFields)
  }

  return (
    <SelectDropdown
      options={fieldOptions}
      initialSelectedOption={selectedFieldOption}
      onOptionSelected={handleOptionSelected}
      disabled={isDescribeLoading || !object}
      loading={isDescribeLoading}
      searchable
      placeholder='Select field...'
    />
  )
}

interface AIGeneratedFieldsTabProps {
  formFields: AIFormValue[]
  connector: string
}

export const AIGeneratedFieldsTab: React.FC<AIGeneratedFieldsTabProps> = ({ formFields, connector }) => {
  const { watch } = useFormContext<FormValues>()
  const formRows = watch('mappingFormFields')

  return (
    <>
      <div className='mb-3'>These fields are automatically filled by AI after your meeting. Choose where each should sync in your CRM.</div>

      {formFields.map((field) => {
        const fieldTitle = field.title
        const match = formRows.find((row) => toMappingKey(row.value ?? '') === toMappingKey(fieldTitle))
        const formRow = match
          ? { ...match, value: fieldTitle }
          : ({
              key: generateId(),
              value: fieldTitle,
              objectName: null,
              fieldName: null,
              type: 'field',
            } as FormRow)
        return <FieldIntegrationRow formRow={formRow} connector={connector} type='field' />
      })}
    </>
  )
}

interface AIGeneratedSingleFieldTabProps {
  formFields: AIFormValue[]
  connector: string
}

export const AIGeneratedSingleFieldTab: React.FC<AIGeneratedSingleFieldTabProps> = ({ formFields, connector }): React.ReactElement => {
  const fieldTitle: string | null = 'all'
  const { watch } = useFormContext<FormValues>()
  const formRows = watch('mappingFormFields')
  const formRow =
    formRows.find((row) => row.value === fieldTitle) ??
    ({
      key: 'all',
      value: fieldTitle,
      objectName: null,
      fieldName: null,
      type: 'field',
    } as FormRow)
  if (!formFields.length) {
    return <div>No field available</div>
  }

  return (
    <>
      <div className='mb-3'>
        All form data will be combined into a single text entry and saved to the field you select. Select a field with sufficient character capacity.
      </div>

      <FieldIntegrationRow formRow={formRow} connector={connector} showFieldTitle={false} type='field' />
    </>
  )
}
